// Queue eligibility (spec 04 §2.1, minus calling hours and the session filter) and ordering (§2.2)
// as a sortable key. SQL twin: public.lead_queue_key (21_gavion_leads_rpc.sql). Keep both in sync.
export type QueueLead = {
  id: number;
  assigned_sales_id: number | null;
  prospect_status: string;
  phone_e164: string | null;
  phone_valid: boolean;
  cooldown_until: string | null;
  attempt_count: number;
  next_callback_at: string | null;
  last_called_at: string | null;
  locked_by: number | null;
  locked_until: string | null;
  high_quality: boolean;
  fit_score: number | null;
  date_added: string | null;
};

export type QueueContext = {
  me: number | null;
  now: Date;
  maxAttempts: number;
  suppressedPhones: ReadonlySet<string>;
};

export const DEFAULT_MAX_ATTEMPTS = 5;
export const QUEUE_EXCLUDED_STATUSES: readonly string[] = Object.freeze([
  "do_not_call",
  "customer",
  "bad_number",
  "demo_booked",
  "in_pipeline", // GAVION call-brief-pipeline (lead_queue_key, 83_)
]);
const RETRY_GAP_MS = 20 * 3600 * 1000;

const ms = (iso: string | null): number | null =>
  iso ? Date.parse(iso) : null;
const stamp = (iso: string): string =>
  new Date(iso).toISOString().replace(/[-:T]/g, "").slice(0, 14);
const pad = (n: number, width: number): string =>
  String(n).padStart(width, "0");

export function queueOrderKey(
  lead: QueueLead,
  ctx: QueueContext,
): string | null {
  const now = ctx.now.getTime();
  const mine = ctx.me !== null && lead.assigned_sales_id === ctx.me;
  if (!lead.phone_e164 || !lead.phone_valid) return null;
  if (QUEUE_EXCLUDED_STATUSES.includes(lead.prospect_status)) return null;
  if (ctx.suppressedPhones.has(lead.phone_e164)) return null;
  const cooldown = ms(lead.cooldown_until);
  if (cooldown !== null && cooldown > now) return null;
  const callback = ms(lead.next_callback_at);
  if (!(lead.attempt_count < ctx.maxAttempts || callback !== null)) return null;
  if (!(lead.assigned_sales_id === null || mine)) return null;
  const lockedUntil = ms(lead.locked_until);
  if (
    !(
      lockedUntil === null ||
      lockedUntil < now ||
      (ctx.me !== null && lead.locked_by === ctx.me)
    )
  )
    return null;

  const id = pad(lead.id, 19);
  if (callback !== null && callback <= now)
    return `${mine ? "1" : "2"}${stamp(lead.next_callback_at as string)}${id}`;
  if (callback !== null) return null; // future callbacks are not served early
  if (lead.attempt_count === 0) {
    const fit =
      lead.fit_score === null
        ? "99999"
        : pad(9999 - Math.min(Math.max(lead.fit_score, 0), 9999), 5);
    const added = lead.date_added
      ? lead.date_added.replace(/-/g, "").slice(0, 8)
      : "99999999";
    return `3${mine ? "0" : "1"}${lead.high_quality ? "0" : "1"}${fit}${added}${id}`;
  }
  // A retry without last_called_at is eligible and sorts as the oldest (ruling, B2-T5).
  const last = ms(lead.last_called_at);
  if (last === null) return `4${"0".repeat(14)}${id}`;
  if (last < now - RETRY_GAP_MS)
    return `4${stamp(lead.last_called_at as string)}${id}`;
  return null;
}

export const isQueueEligible = (lead: QueueLead, ctx: QueueContext): boolean =>
  queueOrderKey(lead, ctx) !== null;
